// CONTRACT:P1-CANONICAL-ENVELOPE.1.0
/** Pure, dependency-free DETERMINISTIC MOTION MODEL (bundle B-VES).
 *
 *  WHAT: a wall-clock PURE function `positionAtMs(rec, absMs)` that returns where a
 *  scenario track is at an ABSOLUTE millisecond time, measured from a fixed compile-time
 *  epoch (MOTION_EPOCH_MS). No `let last = Date.now()`, no dt accumulation, no in-place
 *  mutation of track state — position is a pure function of (record, absMs).
 *
 *  WHY: motion used to be an INCREMENTAL state machine (step(t, dt) mutating t.lat/t.lon/
 *  wpIndex). Two operators logging in 60s apart diverged from tick 1 — each client's
 *  picture depended on WHEN it started ticking, so the shared COP was never actually
 *  shared. Anchoring every position to a fixed epoch makes the picture COHERENT: any
 *  client, at any wall-clock time, computes the SAME position for the SAME track.
 *
 *  This replicates the EXISTING visual behaviour of hormuz.ts/theaters.ts `step()` — the
 *  ONLY change is determinism. Three motion models from the corpus are handled:
 *    - WAYPOINTS (pingpong / loop / once): travel `speedKn` along Haversine legs, modulo
 *      the full loop distance, linear-interpolate on the current leg. `once` is treated as
 *      `loop` (the legacy step() behaviour — no stop-at-last-wp freeze).
 *    - HEADING (static zero-speed vs fast movers): static → unchanged; moving → wrap
 *      elapsed by `duration_s` then straight-line displace from the start. (Wrapping FIXES
 *      the latent bug where fast aircraft drifted off-screen forever — an intentional
 *      improvement.)
 *    - ORBIT (top-level `orbit:true`): an AUTOPILOT-STYLE RACETRACK — 2 straight legs + 2
 *      semicircle turnarounds (the stadium pattern real loitering assets fly), NOT a constant-
 *      radius circle. The authored `motion.radius_nm` is the turn radius, `motion.center`/
 *      `start_bearing_deg` the pattern centre + long axis, `straight_nm` (default r*4) the leg
 *      length. This SUPERSEDES the legacy constant-curvature circle (orbitPosition, retained
 *      for reference) whose tangent heading spun continuously — a tight "curl", not a patrol.
 *      Zero-speed → unchanged.
 *
 *  PURE: imports nothing, deterministic, safe to import from any plane. The scenario
 *  loaders call positionAtMs(track.rec, Date.now()) each tick and assign the result. */

/** A flat {lat, lon} point as authored in the corpus JSON (NOT GeoJSON order). */
export interface MotionPoint {
  lat: number;
  lon: number;
}

/** The authored motion block (subset positionAtMs reads). Field names match the corpus
 *  JSON (hormuz.json / scenario-*.json). */
export interface MotionDef {
  model?: "waypoints" | "heading" | "orbit" | "racetrack";
  speed_kn?: number;
  loop?: "pingpong" | "loop" | "once";
  waypoints?: MotionPoint[];
  heading_deg?: number;
  start?: MotionPoint;
  duration_s?: number;
  /** ORBIT / RACETRACK: the authored geographic centre of the loiter pattern (NOT the seed). */
  center?: MotionPoint;
  /** ORBIT: the authored loiter radius in nautical miles. When present, the orbit is drawn
   *  at THIS radius around `center` (honoured by orbitPosition), not the speed-derived one.
   *  RACETRACK: the turn radius (half the pattern width) at each end. */
  radius_nm?: number;
  /** ORBIT / RACETRACK: turn sense. "ccw" reverses the traverse; default "cw". */
  direction?: "cw" | "ccw";
  /** ORBIT: the compass bearing (from centre) of the loiter point at the epoch.
   *  RACETRACK: the bearing of the straight legs (the pattern's long axis). */
  start_bearing_deg?: number;
  /** RACETRACK: the length (nm) of each straight leg. With `radius_nm` this defines the
   *  stadium/oblong (straight → 180° turn → straight → 180° turn). Authored by the scenario
   *  realism scrubber (scrub.ts) when converting an orbit → an autopilot-style racetrack. */
  straight_nm?: number;
}

/** A structural view of the fields positionAtMs needs from a scenario record. Both the
 *  HormuzRecord and the TheaterRecord shapes satisfy this (duck-typed) — motion.ts stays
 *  decoupled from the scenario record types. */
export interface MotionRecord {
  lat: number;
  lon: number;
  headingDeg: number;
  speedKn: number;
  orbit?: boolean;
  motion?: MotionDef;
}

/** The pure result of a motion evaluation. */
export interface MotionState {
  lat: number;
  lon: number;
  headingDeg: number;
}

/** Fixed compile-time epoch (a fixed past instant). elapsedMs = absMs - MOTION_EPOCH_MS.
 *  Anchoring all motion to this constant is what makes the picture coherent across
 *  sessions — every client computes positions from the SAME zero. Do NOT key this to
 *  process start or Date.now(). */
export const MOTION_EPOCH_MS = 1_750_000_000_000;

/** Default straight-line wrap period for theater movers (24h) — see straightLinePositionAtMs. */
export const STRAIGHT_LINE_PERIOD_MS = 24 * 60 * 60 * 1000;

/** Orbit turn rate (deg/s), replicating the legacy step(): heading += 6 * dt/1000. */
const ORBIT_RATE_DEG_PER_S = 6;

const NM_PER_DEG_LAT = 60;
const DEG2RAD = Math.PI / 180;

/** Web-Mercator's own latitude limit (deg). The flat-earth longitude scaling divides by
 *  cos(lat), which collapses toward 0 at the poles and would yield nonsensical / Infinity
 *  longitudes for a near-polar record. Clamp the cos factor to this limit — it is exactly
 *  where the basemap stops rendering anyway, and it has NO effect on the current corpus
 *  (|lat| ≤ ~56). Pure + total: positionAtMs never divides by ~0. */
const MAX_MERCATOR_LAT = 85;

/** Nautical miles per degree of longitude at `lat`, with the cos factor clamped away from
 *  the poles so the projection never divides by ~0. */
function lonNmPerDeg(lat: number): number {
  const clamped = Math.max(-MAX_MERCATOR_LAT, Math.min(MAX_MERCATOR_LAT, lat));
  return NM_PER_DEG_LAT * Math.cos(clamped * DEG2RAD);
}

/** Always-non-negative modulo (handles absMs < MOTION_EPOCH_MS deterministically). */
function posmod(x: number, m: number): number {
  return ((x % m) + m) % m;
}

/** Normalise a bearing to [0,360). */
function wrapDeg(d: number): number {
  return ((d % 360) + 360) % 360;
}

/** Flat-earth nautical-mile distance (identical formula to the legacy step()). */
function haversineNm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const dLat = (lat2 - lat1) * NM_PER_DEG_LAT;
  const dLon = (lon2 - lon1) * NM_PER_DEG_LAT * Math.cos(((lat1 + lat2) / 2) * DEG2RAD);
  return Math.hypot(dLat, dLon);
}

/** Compass bearing from (lat1,lon1) toward (lat2,lon2) (identical to the legacy step()). */
function bearingDeg(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const y = (lon2 - lon1) * Math.cos(((lat1 + lat2) / 2) * DEG2RAD);
  const x = lat2 - lat1;
  return wrapDeg(Math.atan2(y, x) * (180 / Math.PI));
}

/** Straight-line displacement (identical projection to the legacy step()): move distNm
 *  from (lat,lon) along headingDeg. lon is scaled by cos(origin lat). */
function displace(lat: number, lon: number, headingDeg: number, distNm: number): MotionState {
  const rad = headingDeg * DEG2RAD;
  const nlat = lat + (distNm * Math.cos(rad)) / NM_PER_DEG_LAT;
  const nlon = lon + (distNm * Math.sin(rad)) / lonNmPerDeg(lat);
  return { lat: nlat, lon: nlon, headingDeg: wrapDeg(headingDeg) };
}

/** Build the closed leg list for a waypoint loop. `pingpong` runs forward then back
 *  (w0→…→w_{n-1}→w_{n-2}→…→w1→w0); everything else (loop / once / undefined) cycles
 *  (w0→…→w_{n-1}→w0) — the legacy advanceWaypoint() behaviour. */
function buildLegs(
  wps: MotionPoint[],
  loop: MotionDef["loop"],
): { legs: { from: MotionPoint; to: MotionPoint; lenNm: number; bearing: number }[]; totalNm: number } {
  const seq: MotionPoint[] =
    loop === "pingpong"
      ? // forward 0..n-1 then back n-2..1, the wrap closes back to w0
        [...wps, ...wps.slice(1, -1).reverse()]
      : [...wps];
  // Close the loop: last point connects back to the first.
  seq.push(wps[0]);
  const legs: { from: MotionPoint; to: MotionPoint; lenNm: number; bearing: number }[] = [];
  let totalNm = 0;
  for (let i = 0; i < seq.length - 1; i++) {
    const from = seq[i];
    const to = seq[i + 1];
    const lenNm = haversineNm(from.lat, from.lon, to.lat, to.lon);
    totalNm += lenNm;
    legs.push({ from, to, lenNm, bearing: bearingDeg(from.lat, from.lon, to.lat, to.lon) });
  }
  return { legs, totalNm };
}

/** Pure position for a WAYPOINTS track at elapsedMs (ms since epoch). */
function waypointPosition(
  wps: MotionPoint[],
  loop: MotionDef["loop"],
  speedKn: number,
  elapsedMs: number,
): MotionState {
  const { legs, totalNm } = buildLegs(wps, loop);
  if (totalNm <= 1e-9 || legs.length === 0) {
    return { lat: wps[0].lat, lon: wps[0].lon, headingDeg: legs[0]?.bearing ?? 0 };
  }
  const distNm = speedKn * (elapsedMs / 3_600_000);
  let d = posmod(distNm, totalNm);
  for (const leg of legs) {
    if (d <= leg.lenNm || leg === legs[legs.length - 1]) {
      const frac = leg.lenNm > 1e-9 ? Math.min(1, d / leg.lenNm) : 0;
      return {
        lat: leg.from.lat + (leg.to.lat - leg.from.lat) * frac,
        lon: leg.from.lon + (leg.to.lon - leg.from.lon) * frac,
        headingDeg: leg.bearing,
      };
    }
    d -= leg.lenNm;
  }
  // Unreachable (d < totalNm always lands on a leg) — fall back to the start.
  return { lat: wps[0].lat, lon: wps[0].lon, headingDeg: legs[0].bearing };
}

/** The full loop time (ms) for a WAYPOINTS record — exposed for the determinism test
 *  (periodicity: positionAtMs(rec, T) ≈ positionAtMs(rec, T + period)). Returns null for
 *  non-waypoint / zero-speed / degenerate records. */
export function waypointLoopPeriodMs(rec: MotionRecord): number | null {
  const m = rec.motion;
  if (m?.model !== "waypoints" || !m.waypoints || m.waypoints.length < 2) return null;
  const speedKn = m.speed_kn ?? rec.speedKn;
  if (speedKn <= 0) return null;
  const { totalNm } = buildLegs(m.waypoints, m.loop);
  if (totalNm <= 1e-9) return null;
  return (totalNm / speedKn) * 3_600_000;
}

/** Constant-radius CIRCLE loiter at elapsedMs — RETAINED FOR REFERENCE ONLY. `orbit:true`
 *  records no longer route here; positionAtMs sends them to racetrackPosition (autopilot-style
 *  stadium) so loiters read as real patrols, not tight constant-curvature "curls". Kept so the
 *  legacy model stays documented + available. The bearing-from-centre advances at 6°/s (one
 *  full revolution every 360/6 = 60s).
 *
 *  AUTHORED loiter (preferred): when `motion.radius_nm` is present, the track sits exactly
 *  `radius_nm` nautical miles from the authored `motion.center` (falling back to the seed
 *  position if no centre is authored) at the turning bearing — i.e. it actually sweeps the
 *  authored circle. This SUPERSEDES the legacy behaviour, which derived a tiny speed/ω radius
 *  from speed and ignored the authored centre, collapsing every patrol to a near-static dot.
 *
 *  LEGACY fallback (no authored radius): the closed-form integral of the old heading-turn-and-
 *  fly step() — a small circle of radius v/ω centred near the START. Periodic at 60s. */
function orbitPosition(rec: MotionRecord, speedKn: number, elapsedMs: number): MotionState {
  const m = rec.motion;
  const s = elapsedMs / 1000;

  // Authored circle: loiter at the authored radius_nm around the authored centre.
  if (m?.radius_nm != null && m.radius_nm > 0) {
    const center = m.center ?? { lat: rec.lat, lon: rec.lon };
    const dir = m.direction === "ccw" ? -1 : 1; // ccw reverses the bearing advance
    const startBearing = m.start_bearing_deg ?? rec.headingDeg;
    const bearing = startBearing + dir * ORBIT_RATE_DEG_PER_S * s; // deg, advances 6°/s
    const p = displace(center.lat, center.lon, bearing, m.radius_nm);
    // Heading is tangent to the circle: ±90° from the radial bearing (sign follows turn sense).
    return { lat: p.lat, lon: p.lon, headingDeg: wrapDeg(bearing + dir * 90) };
  }

  // Legacy fallback: speed-derived tiny loiter centred near the START position.
  const omegaRad = ORBIT_RATE_DEG_PER_S * DEG2RAD; // rad/s
  const theta0 = rec.headingDeg; // deg
  const thetaNow = theta0 + ORBIT_RATE_DEG_PER_S * s; // deg
  const vNmPerS = speedKn / 3600;
  const R = vNmPerS / omegaRad; // nm (loiter radius)
  const t0 = theta0 * DEG2RAD;
  const tN = thetaNow * DEG2RAD;
  const northNm = R * (Math.sin(tN) - Math.sin(t0));
  const eastNm = R * (Math.cos(t0) - Math.cos(tN));
  return {
    lat: rec.lat + northNm / NM_PER_DEG_LAT,
    lon: rec.lon + eastNm / lonNmPerDeg(rec.lat),
    headingDeg: wrapDeg(thetaNow),
  };
}

/** RACETRACK geometry — a stadium/oblong loiter: a straight leg, a 180° turnaround at the
 *  authored `radius_nm`, a straight return leg, and a second 180° turnaround. This is the
 *  autopilot-style pattern real surveillance/patrol assets actually fly (NOT a constant-radius
 *  circle). The scenario realism scrubber (scrub.ts) converts authored orbits into racetracks.
 *
 *  Parametrised in a local (along-axis u, cross-axis v) frame centred on `motion.center`, with
 *  the long axis along `start_bearing_deg` (fallback heading). The perimeter is
 *  P = 2·straight + 2·π·radius; distance travelled = speed·elapsed mod P maps onto one of four
 *  segments. PURE + periodic at P/speed (see racetrackLoopPeriodMs). Heading is the segment
 *  tangent. `direction:"ccw"` reverses the traverse. */
/** Stable [0,1) hash (fnv-1a). Used to derive per-entity motion VARIETY (orbit aspect ratio,
 *  spin direction) from the authored seed position — varied per track yet identical on every
 *  client (epoch-anchored determinism: no Date.now()/Math.random() in the motion path). */
function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 100000) / 100000;
}

/** The racetrack straight-leg length (nm): authored `straight_nm`, else a per-entity varied
 *  aspect ratio r*[4..9] seeded (deterministically) from the start position — kept ≥4r so each
 *  orbit stays a recognizable elongated racetrack, not a near-circle. SHARED by racetrackPosition
 *  and racetrackLoopPeriodMs so a track's position and its loop period stay consistent. */
function racetrackStraightNm(rec: MotionRecord, r: number): number {
  const s = rec.motion?.straight_nm;
  if (s != null && s > 0) return s;
  return r * (4 + 5 * hash01(`${rec.lat.toFixed(4)}_${rec.lon.toFixed(4)}shape`));
}

function racetrackPosition(rec: MotionRecord, speedKn: number, elapsedMs: number): MotionState {
  const m = rec.motion;
  const center = m?.center ?? { lat: rec.lat, lon: rec.lon };
  const r = m?.radius_nm != null && m.radius_nm > 0 ? m.radius_nm : 1;
  // Per-entity variety (deterministic): a hash-seeded aspect ratio (racetrackStraightNm) plus a
  // 50/50 CW/CCW default spin instead of always-CW, so orbits don't all read as the same oblong.
  const L = racetrackStraightNm(rec, r);
  const axis = m?.start_bearing_deg ?? rec.headingDeg ?? 0;
  const ccw =
    m?.direction === "ccw"
      ? true
      : m?.direction === "cw"
        ? false
        : hash01(`${rec.lat.toFixed(4)}_${rec.lon.toFixed(4)}dir`) < 0.5;

  const perimeter = 2 * L + 2 * Math.PI * r;
  const distNm = speedKn * (elapsedMs / 3_600_000);
  let s = posmod(distNm, perimeter);
  if (ccw) s = perimeter - s; // reverse the traverse for ccw

  // Local (u along the long axis, v across). Heading is the local travel direction in
  // axis-relative degrees (0 = +u), converted to a compass bearing at the end.
  let u: number;
  let v: number;
  let localHeadingDeg: number; // 0 = +u (axis), 90 = +v
  const turn = Math.PI * r;
  if (s < L) {
    // Leg A — top straight, travelling +u at v = +r.
    u = -L / 2 + s;
    v = r;
    localHeadingDeg = 0;
  } else if (s < L + turn) {
    // Turn 1 — right end semicircle, top(+r) → bottom(-r), bulging past +u.
    const theta = (s - L) / r; // 0..π
    u = L / 2 + r * Math.sin(theta);
    v = r * Math.cos(theta);
    // Travel direction (CCW angle from +u) rotates 0° → -180° across the turn: at entry it is
    // still +u (continuous with Leg A's 0°), at exit it is -u (continuous with Leg B's 180°).
    localHeadingDeg = -(theta * 180) / Math.PI; // 0° → -180° (forward → reverse)
  } else if (s < 2 * L + turn) {
    // Leg B — bottom straight, travelling -u at v = -r.
    const sb = s - (L + turn);
    u = L / 2 - sb;
    v = -r;
    localHeadingDeg = 180;
  } else {
    // Turn 2 — left end semicircle, bottom(-r) → top(+r), bulging past -u.
    const theta = (s - (2 * L + turn)) / r; // 0..π
    u = -L / 2 - r * Math.sin(theta);
    v = -r * Math.cos(theta);
    // Travel direction (CCW angle from +u) rotates 180° → 0° across the turn: at entry it is
    // still -u (continuous with Leg B's 180°), at exit it is +u (continuous with Leg A's 0°).
    localHeadingDeg = 180 - (theta * 180) / Math.PI; // 180° → 0° (reverse → forward)
  }

  // Rotate the local (u along `axis`, v 90° to the LEFT of axis) frame into geographic
  // north/east, then to lat/lon around `center`.
  const ar = axis * DEG2RAD;
  const north = u * Math.cos(ar) + v * Math.sin(ar);
  const east = u * Math.sin(ar) - v * Math.cos(ar);
  const lat = center.lat + north / NM_PER_DEG_LAT;
  const lon = center.lon + east / lonNmPerDeg(center.lat);

  // Compose the compass heading: the local travel heading is measured from +u (== `axis`);
  // v is to the LEFT (axis-90), so a local heading of 90° (toward +v) is a compass bearing
  // of axis-90. Hence compass = axis - localHeading (then wrapped). ccw reverses it 180°.
  const compass = wrapDeg(axis - localHeadingDeg + (ccw ? 180 : 0));
  return { lat, lon, headingDeg: compass };
}

/** The full loop time (ms) for a RACETRACK-flying record — exposed for the periodicity test.
 *  A record flies a racetrack when it authors `model:"racetrack"` OR carries the top-level
 *  `orbit` flag (orbits are now routed through racetrackPosition — see positionAtMs), so both
 *  cases get a finite period. Returns null for everything else / zero-speed / degenerate. */
export function racetrackLoopPeriodMs(rec: MotionRecord): number | null {
  const m = rec.motion;
  // orbit records (routed to racetrack) may carry no motion block at all — keep `m` optional.
  if (m?.model !== "racetrack" && rec.orbit !== true) return null;
  const speedKn = m?.speed_kn ?? rec.speedKn;
  if (speedKn <= 0) return null;
  const r = m?.radius_nm != null && m.radius_nm > 0 ? m.radius_nm : 1;
  const L = racetrackStraightNm(rec, r); // SAME varied length racetrackPosition flies
  const perimeter = 2 * L + 2 * Math.PI * r;
  if (perimeter <= 1e-9) return null;
  return (perimeter / speedKn) * 3_600_000;
}

/** Pure position for a HEADING track at elapsedMs. Static (speed ≤ 0) → unchanged. Moving →
 *  wrap elapsed by duration_s (so fast movers loop instead of drifting off-screen forever)
 *  then straight-line displace from the start at heading. */
function headingPosition(rec: MotionRecord, speedKn: number, elapsedMs: number): MotionState {
  const m = rec.motion;
  const durMs = (m?.duration_s ?? STRAIGHT_LINE_PERIOD_MS / 1000) * 1000;
  const effMs = durMs > 0 ? posmod(elapsedMs, durMs) : elapsedMs;
  const distNm = speedKn * (effMs / 3_600_000);
  const heading = m?.heading_deg ?? rec.headingDeg;
  const start = m?.start ?? { lat: rec.lat, lon: rec.lon };
  return displace(start.lat, start.lon, heading, distNm);
}

/** THE pure motion function. Returns the position/heading of `rec` at absolute time `absMs`
 *  (wall-clock ms, e.g. Date.now()). Deterministic: same (rec, absMs) ⇒ same result on any
 *  client. Dispatches on the corpus motion model (orbit flag → waypoints → heading/static). */
export function positionAtMs(rec: MotionRecord, absMs: number): MotionState {
  const elapsedMs = absMs - MOTION_EPOCH_MS;
  const m = rec.motion;
  const speedKn = m?.speed_kn ?? rec.speedKn;

  // Orbit (top-level flag) — an AUTOPILOT-STYLE RACETRACK (2 straight legs + 2 semicircle
  // turnarounds), NOT a constant-radius circle. Real loitering assets fly racetracks; the
  // legacy circle (orbitPosition — kept below for reference, no longer the default) collapsed
  // every patrol to a tight constant-curvature "curl" whose tangent heading spun continuously.
  // The orbit's radius_nm is reused as the racetrack turn radius, center/start_bearing_deg as
  // the pattern centre + long axis; straight_nm defaults to r*4 (see racetrackPosition).
  // Zero-speed → static.
  if (rec.orbit) {
    if (speedKn <= 0) return { lat: rec.lat, lon: rec.lon, headingDeg: rec.headingDeg };
    return racetrackPosition(rec, speedKn, elapsedMs);
  }

  // Racetrack — autopilot-style stadium loiter (straight / turn / straight / turn).
  if (m?.model === "racetrack") {
    if (speedKn <= 0) return { lat: rec.lat, lon: rec.lon, headingDeg: rec.headingDeg };
    return racetrackPosition(rec, speedKn, elapsedMs);
  }

  // Waypoints — travel the legs.
  if (m?.model === "waypoints" && m.waypoints && m.waypoints.length > 1) {
    if (speedKn <= 0) {
      return { lat: m.waypoints[0].lat, lon: m.waypoints[0].lon, headingDeg: rec.headingDeg };
    }
    return waypointPosition(m.waypoints, m.loop, speedKn, elapsedMs);
  }

  // Heading / default — static or straight-line (duration-wrapped).
  if (speedKn <= 0) return { lat: rec.lat, lon: rec.lon, headingDeg: rec.headingDeg };
  return headingPosition(rec, speedKn, elapsedMs);
}

/** Pure STRAIGHT-LINE position for theater movers (the Taiwan/Levant loaders fly straight,
 *  ignoring waypoint/orbit blocks — preserving the legacy theaters.ts step() behaviour). The
 *  motion is wrapped by `periodMs` (default 24h) so it is bounded/periodic rather than
 *  drifting forever. Zero-speed → unchanged. Uses rec.speedKn / rec.headingDeg (the fields
 *  the theater loader carries), NOT the authored motion block. */
export function straightLinePositionAtMs(
  rec: MotionRecord,
  absMs: number,
  periodMs: number = STRAIGHT_LINE_PERIOD_MS,
): MotionState {
  if (rec.speedKn <= 0) return { lat: rec.lat, lon: rec.lon, headingDeg: rec.headingDeg };
  const elapsedMs = absMs - MOTION_EPOCH_MS;
  const effMs = periodMs > 0 ? posmod(elapsedMs, periodMs) : elapsedMs;
  const distNm = rec.speedKn * (effMs / 3_600_000);
  return displace(rec.lat, rec.lon, rec.headingDeg, distNm);
}

/** Pure POLL-ANCHORED forward dead-reckon: advance (lat,lon) along `headingDeg` at `speedKn`
 *  for `elapsedMs` since the last FIX. Unlike positionAtMs this uses a LOCAL clock (elapsed
 *  since the caller's fix), NOT the global MOTION_EPOCH — so it starts at 0 displacement at the
 *  fix and flies forward. `periodMs` wraps the excursion (default 24h) so a source that stops
 *  refreshing (e.g. ADS-B offline on the bundled snapshot) LOOPS within a bounded distance
 *  instead of flying off-scope forever. Static / non-finite ⇒ unchanged. */
export function deadReckon(
  lat: number,
  lon: number,
  headingDeg: number,
  speedKn: number,
  elapsedMs: number,
  periodMs: number = STRAIGHT_LINE_PERIOD_MS,
): MotionState {
  if (!(speedKn > 0) || !Number.isFinite(elapsedMs) || !Number.isFinite(headingDeg)) {
    return { lat, lon, headingDeg: wrapDeg(headingDeg || 0) };
  }
  const effMs = periodMs > 0 ? posmod(elapsedMs, periodMs) : Math.max(0, elapsedMs);
  const distNm = speedKn * (effMs / 3_600_000);
  return displace(lat, lon, headingDeg, distNm);
}

// ── HYPERSONIC BLIP CADENCE ─────────────────────────────────────────────────────────────
/** Tuning for the hypersonic-track blip pulse. The cadence (blips per second) scales LINEARLY
 *  with speed and is CLAMPED to a floor + ceiling so a sensor blip never strobes faster than
 *  the eye can read, nor goes dead-slow for a merely-fast mover. */
export const BLIP = {
  /** Speed (kn) at/below which the blip pulses at the floor rate. */
  floorSpeedKn: 600,
  /** Speed (kn) at/above which the blip pulses at the ceiling rate. */
  ceilSpeedKn: 4000,
  /** Blip rate (Hz) at floorSpeedKn. */
  floorHz: 0.5,
  /** Blip rate (Hz) at/above ceilSpeedKn (the clamp). */
  ceilHz: 2.5,
  /** Fraction of each period the blip is "lit" (the flash duty cycle). */
  duty: 0.45,
} as const;

/** The blip RATE (Hz) for a track of the given speed — cadence ∝ speed, LINEAR between
 *  floorSpeedKn..ceilSpeedKn and clamped to [floorHz, ceilHz] outside it. PURE + total. */
export function blipRateHz(speedKn: number): number {
  const s = Number.isFinite(speedKn) ? speedKn : 0;
  const span = BLIP.ceilSpeedKn - BLIP.floorSpeedKn;
  const t = span > 0 ? (s - BLIP.floorSpeedKn) / span : 1;
  const tc = t < 0 ? 0 : t > 1 ? 1 : t; // clamp 0..1
  return BLIP.floorHz + (BLIP.ceilHz - BLIP.floorHz) * tc;
}

/** The deterministic blip state for a hypersonic track at absolute time `absMs`. Like the rest
 *  of this module it is EPOCH-ANCHORED (phase measured from MOTION_EPOCH_MS) so two clients
 *  sampling the same wall-clock instant see the SAME blip — the pulse is coherent across the
 *  shared COP, not a per-session animation.
 *
 *  Returns:
 *   - phase     : [0,1) position within the current blip period.
 *   - intensity : [0,1] flash envelope — bright at the start of the period (a triangular
 *                 attack/decay over the duty window), 0 in the gap. Drives the blip's alpha.
 *   - lit       : whether the blip is in its lit (duty) window.
 *
 *  cadence ∝ speed: higher speed ⇒ higher blipRateHz ⇒ shorter period ⇒ blips MORE often. */
export function hypersonicBlipPhase(
  absMs: number,
  speedKn: number,
): { phase: number; intensity: number; lit: boolean } {
  const hz = blipRateHz(speedKn);
  const periodMs = hz > 0 ? 1000 / hz : 1000;
  const elapsedMs = absMs - MOTION_EPOCH_MS;
  const phase = posmod(elapsedMs, periodMs) / periodMs; // [0,1)
  const lit = phase < BLIP.duty;
  // Triangular flash inside the duty window: ramp up to mid-duty, ramp down — a clean pulse.
  let intensity = 0;
  if (lit) {
    const x = phase / BLIP.duty; // 0..1 across the lit window
    intensity = x < 0.5 ? x / 0.5 : (1 - x) / 0.5;
  }
  return { phase, intensity, lit };
}
